import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Eye, EyeOff, User, Mail, Phone, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { PhoneInput } from '@/components/ui/phone-input';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { User as UserType, Role } from '@/types/auth';
import { useCreateAdminUser } from '@/api-services/hooks/useAdminUsers';
import { UserDTO } from '@/api-services/hooks/adminTypes';
import { useRolesDropdown } from '@/api-services/hooks/useRoles';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { useDuplicateCheck } from '@/hooks/useDuplicateCheck';

interface AddUserForm {
  userName: string;
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  phoneCountryCode: string;
  password: string;
  confirmPassword: string;
  roles: string[];
  rolesAssigned: string;
  status: string;
  activated: boolean;
}

const AddUserPage: React.FC = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { duplicateErrors, checkEmail, checkPhone } = useDuplicateCheck('admin');
  const emailRef = React.useRef<HTMLInputElement>(null);
  const phoneRef = React.useRef<HTMLDivElement>(null);

  // Fetch active roles from API
  const { data: rolesData, loading: rolesLoading } = useRolesDropdown();

  const [formData, setFormData] = useState<AddUserForm>({
    userName: '',
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    phoneCountryCode: '',
    password: '',
    confirmPassword: '',
    rolesAssigned: '',
    roles: [],
    status: 'active',
    activated: true,
  });

  // Create user mutation
  const createUserMutation = useCreateAdminUser({
    onSuccess: (response) => {
      if(response.status === 'error'){
        toast.error(response.message);
      }else{
        toast.success('User created successfully!');
        navigate('/admin/users/list');
      }
    },
    onError: (error: any) => {
      console.error('Failed to create user:', error);
      let errorMessage = 'Failed to create user';
      const status = error?.response?.status;
      const data = error?.response?.data;
      if (status === 400) {
        errorMessage = data?.message || 'Invalid user data. Please check all fields.';
      } else if (status === 409) {
        errorMessage = 'User already exists. Email, username, or phone may be duplicate.';
      } else if (status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error?.message) {
        errorMessage = error.message;
      }
      const lowerMessage = errorMessage.toLowerCase();
      if (lowerMessage.includes('email') && lowerMessage.includes('unique')) {
        setErrors({ email: 'This email is already in use. Please use a different email.' });
        toast.error('Email address is already registered');
      } else if (lowerMessage.includes('username') && lowerMessage.includes('unique')) {
        setErrors({ userName: 'This username is already taken. Please choose a different one.' });
        toast.error('Username is already taken');
      } else if (lowerMessage.includes('phone') && lowerMessage.includes('unique')) {
        setErrors({ mobile: 'This phone number is already in use.' });
        toast.error('Phone number is already registered');
      } else {
        toast.error(errorMessage);
      }
    },
  });

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.userName.trim()) {
      newErrors.userName = 'Username is required';
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.mobile.trim()) {
      newErrors.mobile = 'Phone number is required';
    } else if (formData.mobile.length > 15) {
      newErrors.mobile = 'Phone number must be 10 digits';
    } else if (!isValidPhoneNumber(formData.mobile)) {
      newErrors.mobile = 'Invalid phone number';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData.rolesAssigned) {
      newErrors.rolesAssigned = 'Role is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the validation errors');
      return;
    }

      let phoneCountryCode = '';
  let mobileNumber = '';

  if (formData.mobile) {
    const phone = parsePhoneNumberFromString(formData.mobile);

    if (phone) {
      phoneCountryCode = `+${phone.countryCallingCode}`; // +91
      mobileNumber = phone.nationalNumber;              // 9876543210
    }
  }

    // Build create payload - role should always be ROLE_USER, rolesAssigned contains selected value
    const createData: UserDTO = {
      userName: formData.userName,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      mobile: mobileNumber,
      phoneCountryCode: phoneCountryCode,
      password: formData.password,
      roles: ['ROLE_USER'], // Always send ROLE_USER
      rolesAssigned: formData.rolesAssigned, // Send the selected role
      type: 'ADMIN',
      isGoogleAccount: false,
    };

    createUserMutation.mutate(createData);
  };

  const handleInputChange = (field: keyof AddUserForm, value: string | boolean) => {
    setFormData(prev => {
      const update: Partial<AddUserForm> = { [field]: value };
      // username mirrors firstName; password & confirmPassword mirror email
      
 // ✅ Only handle phone logic when field is 'mobile'
    if (field === 'mobile' && typeof value === 'string') {
      const phone = parsePhoneNumberFromString(value);

      if (phone) {
        const mobile = phone.nationalNumber;
        update.mobile = value;          
        update.userName = mobile;        // mirror to username
        update.phoneCountryCode = `+${phone.countryCallingCode}`; // optional 🔥
      }
    }
    // if (field === 'email') { update.password = value as string; update.confirmPassword = value as string; }
      return { ...prev, ...update };
    });
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleRoleChange = (roleName: string) => {
    setFormData(prev => ({ ...prev, rolesAssigned: roleName }));
    if (errors.rolesAssigned) {
      setErrors(prev => ({ ...prev, rolesAssigned: '' }));
    }
  };

  const getSelectedRoleName = (): string => {
    return formData.rolesAssigned || '';
  };

  const loading = createUserMutation.loading;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/admin/users/list')}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Users
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Add New User</h1>
          <p className="text-muted-foreground">
            Create a new user account with specified role and permissions
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>User Information</CardTitle>
            <CardDescription>
              Enter the basic information for the new user account
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Username, First Name, Last Name */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="userName" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Username <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="userName"
                  value={formData.userName}
                  readOnly
                  placeholder="Auto-filled from phone number"
                  className={`bg-muted cursor-not-allowed ${errors.userName ? 'border-red-500' : ''}`}
                  autoComplete="off"
                />
                {errors.userName && (
                  <p className="text-sm text-red-500">{errors.userName}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="firstName" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  First Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  placeholder="Enter first name"
                  className={errors.firstName ? 'border-red-500' : ''}
                  autoComplete="off"
                />
                {errors.firstName && (
                  <p className="text-sm text-red-500">{errors.firstName}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Last Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  placeholder="Enter last name"
                  className={errors.lastName ? 'border-red-500' : ''}
                  autoComplete="off"
                />
                {errors.lastName && (
                  <p className="text-sm text-red-500">{errors.lastName}</p>
                )}
              </div>
            </div>

            {/* Email and Phone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email Address <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  ref={emailRef}
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  onBlur={async () => {
                    const dup = await checkEmail(formData.email);
                    if (dup) emailRef.current?.focus();
                  }}
                  placeholder="user@example.com"
                  className={(errors.email || duplicateErrors.email) ? 'border-red-500' : ''}
                  autoComplete="off"
                />
                {(errors.email || duplicateErrors.email) && (
                  <p className="text-sm text-red-500">{errors.email || duplicateErrors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="mobile" className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Phone Number <span className="text-red-500">*</span>
                </Label>
                <PhoneInput
                  ref={phoneRef}
                  id="mobile"
                  value={formData.mobile}
                  onChange={(val) => handleInputChange('mobile', val || '')}
                  onBlur={async () => {
                    const dup = await checkPhone(formData.mobile);
                    if (dup) phoneRef.current?.querySelector('input')?.focus();
                  }}
                  hasError={!!(errors.mobile || duplicateErrors.phone)}
                />
                {(errors.mobile || duplicateErrors.phone) && (
                  <p className="text-sm text-red-500">{errors.mobile || duplicateErrors.phone}</p>
                )}
              </div>
            </div>

            {/* Role */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="role" className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Role <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={getSelectedRoleName()}
                  onValueChange={handleRoleChange}
                  disabled={rolesLoading}
                >
                  <SelectTrigger className={errors.rolesAssigned ? 'border-red-500' : ''}>
                    <SelectValue placeholder={rolesLoading ? 'Loading roles...' : 'Select user role'} />
                  </SelectTrigger>
                  <SelectContent>
                    {(rolesData || []).map((role) => (
                      <SelectItem key={role.roleId} value={role.roleName}>
                        {role.roleName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.rolesAssigned && (
                  <p className="text-sm text-red-500">{errors.rolesAssigned}</p>
                )}
              </div>
            </div>

            {/* Password Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="password">Password <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    placeholder="Enter Password"
                    className={`${errors.password ? 'border-red-500 pr-10' : 'pr-10'}`}
                    autoComplete="new-password"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                {errors.password && (
                  <p className="text-sm text-red-500">{errors.password}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    placeholder="Confirm password"
                    className={`${errors.confirmPassword ? 'border-red-500 pr-10' : 'pr-10'}`}
                    autoComplete="new-password"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-sm text-red-500">{errors.confirmPassword}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/admin/users/list')}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Creating User...' : 'Create User'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddUserPage;
